/* =========================================================================
 * IBC 2.0 — REVEAL
 * -------------------------------------------------------------------------
 * Content arrives as you scroll to it. Headings marked `data-ibc-split`
 * rise word by word out of a mask; everything else — paragraphs,
 * photographs, rows of buttons — arrives as one quiet piece.
 *
 * Two rules hold whatever the mode:
 *   · every word is real text in the DOM, in order, with real spaces, so it
 *     is selectable, searchable and read correctly by a screen reader
 *   · `prefers-reduced-motion` shows everything at once, immediately
 *
 * Idempotent: safe to run again after a Theme Editor section reload.
 * Cleans up on `shopify:section:unload`.
 * ====================================================================== */
(function () {
  'use strict';

  var MARK = 'ibcReveal'; // dataset flag — element already processed
  var MAX_CHARS = 900; // beyond this a block is long-form copy, not a heading
  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** Per-word stagger, tightened on long lines so nothing takes > ~3/4s. */
  function stagger(count, mask) {
    var span = mask ? 620 : 560;
    var min = mask ? 22 : 8;
    var max = mask ? 62 : 26;
    return Math.min(max, Math.max(min, span / Math.max(count, 1)));
  }

  /** Replace an element's text with masked, staggered words. */
  function split(el, mask, base) {
    var text = el.textContent || '';
    if (!text.trim() || text.length > MAX_CHARS) return false;

    /* Text, and <br> between it. A heading that carries a deliberate line
       break is still split word by word; the break is carried through into
       the rebuilt markup. Anything ELSE inside — a link, a <span>, a
       comment — still moves as one quiet piece, exactly as before. */
    var kids = el.childNodes;
    var i;
    for (i = 0; i < kids.length; i++) {
      if (kids[i].nodeType === 3) continue;
      if (kids[i].nodeType === 1 && kids[i].tagName === 'BR') continue;
      return false;
    }

    /* Count every word before building any of them, so the stagger is spread
       across the whole heading rather than restarting after each break. */
    var runs = [];
    var count = 0;
    for (i = 0; i < kids.length; i++) {
      if (kids[i].nodeType !== 3) {
        runs.push(null); /* a line break */
        continue;
      }
      var run = (kids[i].textContent || '').trim();
      run = run ? run.split(/\s+/) : [];
      if (!run.length) continue; /* whitespace either side of a <br> */
      runs.push(run);
      count += run.length;
    }
    if (!count) return false;

    var step = stagger(count, mask);
    var frag = document.createDocumentFragment();
    var placed = 0;
    var afterWord = false;

    for (var r = 0; r < runs.length; r++) {
      if (runs[r] === null) {
        /* A real space before the break as well, so the heading's text still
           reads as separate words when it is copied, searched on the page or
           spoken. Trailing whitespace at the end of a line is not drawn, so
           it costs nothing on screen. */
        if (afterWord) frag.appendChild(document.createTextNode(' '));
        frag.appendChild(document.createElement('br'));
        afterWord = false;
        continue;
      }
      for (var w = 0; w < runs[r].length; w++) {
        /* The space between words is a real text node outside the mask, so
           lines break exactly where they always did. None before the first
           word, and none either side of a break, which carries its own. */
        if (afterWord) frag.appendChild(document.createTextNode(' '));
        var outer = document.createElement('span');
        outer.className = 'sr__w';
        var inner = document.createElement('span');
        inner.className = 'sr__wi';
        inner.style.transitionDelay = Math.round(base + placed * step) + 'ms';
        inner.textContent = runs[r][w];
        outer.appendChild(inner);
        frag.appendChild(outer);
        afterWord = true;
        placed++;
      }
    }

    el.textContent = '';
    el.appendChild(frag);
    el.classList.add(mask ? 'sr-mask' : 'sr-fade');
    return true;
  }

  /** Wrap an element's children in one delayed piece. */
  function whole(el, base) {
    if (!el.childNodes.length) return false;
    var inner = document.createElement('span');
    inner.className = 'sr__wi sr__wi--all';
    inner.style.transitionDelay = base + 'ms';
    while (el.firstChild) inner.appendChild(el.firstChild);
    el.appendChild(inner);
    el.classList.add('sr-fade');
    return true;
  }

  var observer = null;

  function getObserver() {
    if (observer) return observer;
    if (!('IntersectionObserver' in window)) return null;
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    return observer;
  }

  /** Prepare one element. Returns true when it was newly prepared. */
  function prepare(el) {
    if (el.dataset[MARK] === 'done') return false;
    el.dataset[MARK] = 'done';

    if (reduced) {
      el.classList.add('is-in');
      return false;
    }

    var base = parseInt(el.getAttribute('data-ibc-delay') || '0', 10) || 0;
    var isText = false;

    if (el.hasAttribute('data-ibc-split')) {
      var tag = el.tagName.toLowerCase();
      var mask = tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4';
      isText = mask ? split(el, true, base) : whole(el, base);
    }

    if (isText) {
      el.classList.add('sr');
    } else {
      el.classList.add('reveal');
      if (base) el.style.setProperty('--reveal-delay', base + 'ms');
    }

    var io = getObserver();
    if (io) {
      io.observe(el);
    } else {
      el.classList.add('is-in');
    }
    return true;
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('[data-ibc-reveal]');
    for (var i = 0; i < nodes.length; i++) prepare(nodes[i]);
  }

  function unload(event) {
    if (!observer) return;
    var scope = event && event.target;
    if (!scope || !scope.querySelectorAll) return;
    var nodes = scope.querySelectorAll('[data-ibc-reveal]');
    for (var i = 0; i < nodes.length; i++) observer.unobserve(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
  document.addEventListener('shopify:section:unload', unload);

  window.IbcReveal = { init: init };
})();
